import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  PLATFORM_ID,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
  FormsModule,
} from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ScrapCertificateService } from '../../services/scrap-certificate.service';
import { CartService } from '../../services/cart.service';
import { LoaderService } from '../../services/loader.service';
import { NotificationService } from '../../services/notification.service';
import { ApiService } from '../../services/api.service';
import { ModalService } from '../../services/modal.service';
import {
  SCRAP_CERT_NAME_PATTERN,
  SCRAP_CERT_NUMBER_PATTERN,
  NAVIGATION_KEYS,
} from '../../constants/scrap-certificate-validation';

@Component({
  selector: 'app-scrap-certificate-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      *ngIf="showScrapCertificateModal"
      class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-gray-500 bg-opacity-50 backdrop-blur-sm p-0 sm:p-2 transition-opacity duration-300"
      [class.animate-fade-in]="showScrapCertificateModal"
      (click)="onBackdropClick($event)"
    >
      <div
        class="w-full sm:w-full sm:max-w-4xl max-h-[90vh] sm:max-h-[90vh] overflow-y-auto bg-white shadow-[2px_2px_12px_rgba(0,0,0,0.10)] py-4 px-4 sm:py-6 sm:px-6 lg:py-8 lg:px-8 relative scroll-bar-none rounded-t-3xl sm:rounded-2xl transform transition-transform duration-300 ease-out"
        [class.animate-slide-up]="showScrapCertificateModal"
        (click)="$event.stopPropagation()"
      >
        <!-- Drag Handle (Mobile Only) -->
        <div class="flex justify-center mb-3 sm:hidden">
          <div class="w-12 h-1.5 bg-gray-300 rounded-full"></div>
        </div>

        <!-- Header -->
        <div class="flex justify-between items-center mb-4 sm:mb-6">
          <h2
            class="text-lg sm:text-xl lg:text-2xl font-bold text-[color:var(--Primary-Deep-Blue,#0A0A5F)] leading-tight"
          >
            Upload Scrap Certificates
          </h2>
          <button
            (click)="closeModal()"
            class="text-gray-500 hover:text-gray-700 text-xl sm:text-2xl p-1"
          >
            ✕
          </button>
        </div>
        <hr />
        <!-- Form -->
        <form [formGroup]="certificateForm" (ngSubmit)="onSubmit()">
          <!-- Scrap Category -->
          <div class="space-y-2 mb-4">
            <label class="block text-sm sm:text-base font-semibold text-[#000]"
              >Scrap Category <span class="text-red-500">*</span></label
            >
            <div class="relative">
              <!-- Desktop: Native select -->
              <select
                formControlName="scrapCategory"
                class="hidden sm:block w-full border rounded-lg px-3 py-2.5 sm:py-3 text-sm sm:text-base text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                [class.border-red-500]="isFieldInvalid('scrapCategory')"
              >
                <option value="">Select any one Category</option>
                <option
                  *ngFor="let category of scrapList"
                  [value]="category?.name"
                >
                  {{ category?.name }}
                </option>
              </select>
              <!-- Mobile: Custom button that opens bottom sheet -->
              <button
                type="button"
                (click)="openCategoryBottomSheet()"
                class="sm:hidden w-full border rounded-lg px-3 py-2.5 text-sm text-gray-700 bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between"
                [class.border-red-500]="isFieldInvalid('scrapCategory')"
              >
                <span
                  [class.text-gray-400]="
                    !certificateForm.get('scrapCategory')?.value
                  "
                  >{{ getSelectedCategoryDisplay() }}</span
                >
                <svg
                  class="w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  ></path>
                </svg>
              </button>
            </div>
            <div
              *ngIf="isFieldInvalid('scrapCategory')"
              class="text-red-500 text-xs sm:text-sm mt-1"
            >
              {{ getFieldError('scrapCategory') }}
            </div>
          </div>

          <!-- Certificate Name and Number -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4">
            <div class="space-y-2">
              <label class="block text-sm sm:text-base font-bold text-zinc-700"
                >Certificate Name <span class="text-red-500">*</span></label
              >
              <input
                type="text"
                formControlName="certificateName"
                placeholder="Enter Certificate name"
                class="w-full p-2.5 sm:p-3 text-sm sm:text-base font-medium text-black rounded-lg border border-solid border-[color:var(--Input-field-Stroke,#D4D4D4)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400 placeholder:font-normal"
                [class.border-red-500]="isFieldInvalid('certificateName')"
                (keydown)="preventLeadingSpace($event, $event.target)"
                (input)="trimLeadingSpace($event, 'certificateName')"
                (blur)="trimLeadingSpace($event, 'certificateName')"
              />
              <div
                *ngIf="isFieldInvalid('certificateName')"
                class="text-red-500 text-xs sm:text-sm mt-1"
              >
                {{ getFieldError('certificateName') }}
              </div>
            </div>
            <div class="space-y-2">
              <label class="block text-sm sm:text-base font-bold text-zinc-700"
                >Certificate No. <span class="text-red-500">*</span></label
              >
              <input
                type="text"
                formControlName="certificateNumber"
                placeholder="Enter Certificate No."
                class="w-full p-2.5 sm:p-3 text-sm sm:text-base font-medium text-black rounded-lg border border-solid border-[color:var(--Input-field-Stroke,#D4D4D4)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400 placeholder:font-normal"
                [class.border-red-500]="isFieldInvalid('certificateNumber')"
                (keydown)="preventLeading($event, $event.target)"
                (input)="trimLeadingSpace($event, 'certificateNumber')"
                (blur)="trimLeadingSpace($event, 'certificateNumber')"
              />
              <div
                *ngIf="isFieldInvalid('certificateNumber')"
                class="text-red-500 text-xs sm:text-sm mt-1"
              >
                {{ getFieldError('certificateNumber') }}
              </div>
            </div>
          </div>

          <!-- Issuing Authority and Company Name -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4">
            <div class="space-y-2">
              <label class="block text-sm sm:text-base font-bold text-zinc-700"
                >Issuing Authority <span class="text-red-500">*</span></label
              >
              <input
                type="text"
                formControlName="issuingAuthority"
                placeholder="Enter issuing authority name"
                class="w-full p-2.5 sm:p-3 text-sm sm:text-base font-medium text-black rounded-lg border border-solid border-[color:var(--Input-field-Stroke,#D4D4D4)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400 placeholder:font-normal"
                [class.border-red-500]="isFieldInvalid('issuingAuthority')"
                (keydown)="preventLeadingSpace($event, $event.target)"
                (input)="trimLeadingSpace($event, 'issuingAuthority')"
                (blur)="trimLeadingSpace($event, 'issuingAuthority')"
              />
              <div
                *ngIf="isFieldInvalid('issuingAuthority')"
                class="text-red-500 text-xs sm:text-sm mt-1"
              >
                {{ getFieldError('issuingAuthority') }}
              </div>
            </div>
            <div class="space-y-2">
              <label class="block text-sm sm:text-base font-bold text-zinc-700"
                >Company Name <span class="text-red-500">*</span></label
              >
              <input
                type="text"
                formControlName="companyName"
                placeholder="Enter company name"
                class="w-full p-2.5 sm:p-3 text-sm sm:text-base font-medium text-black rounded-lg border border-solid border-[color:var(--Input-field-Stroke,#D4D4D4)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400 placeholder:font-normal"
                [class.border-red-500]="isFieldInvalid('companyName')"
                (keydown)="preventLeadingSpace($event, $event.target)"
                (input)="trimLeadingSpace($event, 'companyName')"
                (blur)="trimLeadingSpace($event, 'companyName')"
              />
              <div
                *ngIf="isFieldInvalid('companyName')"
                class="text-red-500 text-xs sm:text-sm mt-1"
              >
                {{ getFieldError('companyName') }}
              </div>
            </div>
          </div>

          <!-- Issue Date and Expiry Date -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4">
            <div class="space-y-2">
              <label class="block text-sm sm:text-base font-bold text-zinc-700"
                >Issue Date <span class="text-red-500">*</span></label
              >
              <div class="relative">
                <input
                  type="date"
                  formControlName="issueDate"
                  class="w-full p-2.5 sm:p-3 text-sm sm:text-base font-medium text-black rounded-lg border border-solid border-[color:var(--Input-field-Stroke,#D4D4D4)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  [class.border-red-500]="isFieldInvalid('issueDate')"
                  (change)="onDateChange()"
                  (click)="openDatePicker('issueDate', $event)"
                />
                <span
                  *ngIf="!certificateForm.get('issueDate')?.value"
                  class="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none sm:hidden"
                >
                  Issue date of certificate
                </span>
              </div>
              <!-- <p class="text-xs sm:text-sm text-gray-500 mt-1">Select issue date of certificate</p> -->
              <div
                *ngIf="isFieldInvalid('issueDate')"
                class="text-red-500 text-xs sm:text-sm mt-1"
              >
                {{ getFieldError('issueDate') }}
              </div>
            </div>
            <div class="space-y-2">
              <label class="block text-sm sm:text-base font-bold text-zinc-700"
                >Expiry Date <span class="text-red-500">*</span></label
              >
              <div class="relative">
                <input
                  type="date"
                  formControlName="expiryDate"
                  class="w-full p-2.5 sm:p-3 text-sm sm:text-base font-medium text-black rounded-lg border border-solid border-[color:var(--Input-field-Stroke,#D4D4D4)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  [class.border-red-500]="isFieldInvalid('expiryDate')"
                  (change)="onDateChange()"
                  (click)="openDatePicker('expiryDate', $event)"
                />
                <span
                  *ngIf="!certificateForm.get('expiryDate')?.value"
                  class="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none sm:hidden"
                >
                  Expiry date of certificate
                </span>
              </div>
              <!-- <p class="text-xs sm:text-sm text-gray-500 mt-1">Select expiry date of certificate</p> -->
              <div
                *ngIf="isFieldInvalid('expiryDate')"
                class="text-red-500 text-xs sm:text-sm mt-1"
              >
                {{ getFieldError('expiryDate') }}
              </div>
            </div>
          </div>

          <!-- Upload Section -->
          <div class="space-y-4 mb-4">
            <label class="block text-sm sm:text-base font-semibold text-[#000]"
              >Certificate Files <span class="text-red-500">*</span></label
            >

            <!-- Main Upload Area - Show when no images are selected -->
            <div
              *ngIf="ImageAray.length === 0"
              class="flex flex-col items-center justify-center border-2 border-dashed border-[color:var(--Primary-Deep-Blue,#0A0A5F)] rounded-lg py-8 sm:py-12 cursor-pointer text-[#307FE1] hover:bg-blue-50 transition-colors"
              (click)="triggerFileInput(mainFileInput)"
            >
              <input
                type="file"
                accept=".jpg,.jpeg,.png"
                #mainFileInput
                class="hidden"
                (change)="onFileUpload($event)"
                multiple
              />

              <!-- Upload Icon -->
              <div class="inline-block">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="48"
                  height="48"
                  viewBox="0 0 56 56"
                  fill="none"
                  class="sm:w-14 sm:h-14"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M14.0848 19.0012C15.3425 19.0012 16.5596 19.1665 17.7191 19.4756C19.4297 13.2683 25.1149 8.70898 31.8631 8.70898C38.6261 8.70898 44.3188 13.2848 46.017 19.5111C49.3135 19.8944 51.8746 22.695 51.8746 26.0944C51.8746 26.9257 51.7212 27.7186 51.4433 28.4518C54.1865 30.306 56 33.4445 56 36.9878C56 42.6513 51.3667 47.2845 45.7032 47.2845H33.0464C32.7077 47.2845 32.4331 47.01 32.4331 46.6712V35.8544C32.4331 35.5157 32.7077 35.2411 33.0464 35.2411H35.2103C35.7461 35.2411 36.0243 34.6023 35.6594 34.21L28.4499 26.4598C28.2074 26.1991 27.7944 26.1991 27.5518 26.4598L20.3424 34.21C19.9775 34.6023 20.2557 35.2411 20.7915 35.2411H22.9553C23.2941 35.2411 23.5687 35.5157 23.5687 35.8544V46.6712C23.5687 47.01 23.2941 47.2845 22.9553 47.2845H13.9716C12.8864 47.2845 11.8394 47.1121 10.8524 46.7982C4.63291 45.3368 0 39.7526 0 33.0878C0 25.3111 6.30539 19.0032 14.0846 19.0032L14.0848 19.0012Z"
                    fill="#307FE1"
                  />
                </svg>
              </div>

              <span
                class="font-semibold text-sm sm:text-base text-[color:var(--Primary-Deep-Blue,#0A0A5F)] mt-2"
                >Upload Certificate</span
              >
            </div>
            <p
              class="text-xs sm:text-sm text-gray-500 mt-2 text-start"
              *ngIf="ImageAray.length === 0"
            >
              Drag files here in JPG/PNG | File Size : Max 5MB
            </p>

            <!-- File Upload Grid - Show when images are selected -->
            <div *ngIf="ImageAray.length > 0" class="space-y-4">
              <div
                class="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-3 sm:gap-4"
              >
                <!-- Add More Upload Box -->
                <div
                  class="relative w-full aspect-square border-dashed border-2 border-[color:var(--Primary-Deep-Blue,#0A0A5F)] rounded-lg cursor-pointer flex flex-col items-center justify-center hover:bg-blue-50 transition-colors"
                  (click)="triggerFileInput(addMoreFileInput)"
                >
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    #addMoreFileInput
                    class="hidden"
                    (change)="onFileSelected($event)"
                    multiple
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="16"
                    viewBox="0 0 56 40"
                    fill="none"
                    class="sm:w-6 sm:h-5"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M14.0848 11.0012C15.3425 11.0012 16.5596 11.1665 17.7191 11.4756C19.4297 5.2683 25.1149 0.708984 31.8631 0.708984C38.6261 0.708984 44.3188 5.28479 46.017 11.5111C49.3135 11.8944 51.8746 14.695 51.8746 18.0944C51.8746 18.9257 51.7212 19.7186 51.4433 20.4518C54.1865 22.306 56 25.4445 56 28.9878C56 34.6513 51.3667 39.2845 45.7032 39.2845H33.0464C32.7077 39.2845 32.4331 39.01 32.4331 38.6712V27.8544C32.4331 27.5157 32.7077 27.2411 33.0464 27.2411H35.2103C35.7461 27.2411 36.0243 26.6023 35.6594 26.21L28.4499 18.4598C28.2074 18.1991 27.7944 18.1991 27.5518 18.4598L20.3424 26.21C19.9775 26.6023 20.2557 27.2411 20.7915 27.2411H22.9553C23.2941 27.2411 23.5687 27.5157 23.5687 27.8544V38.6712C23.5687 39.01 23.2941 39.2845 22.9553 39.2845H13.9716C12.8864 39.2845 11.8394 39.1121 10.8524 38.7982C4.63291 37.3368 0 31.7526 0 25.0878C0 17.3111 6.30539 11.0032 14.0846 11.0032L14.0848 11.0012Z"
                      fill="#307FE1"
                    />
                  </svg>
                  <span
                    class="font-semibold text-xs sm:text-sm mt-1 text-[color:var(--Primary-Deep-Blue,#0A0A5F)]"
                    >Add More</span
                  >
                </div>

                <!-- Uploaded Images -->
                <div
                  *ngFor="let img of ImageAray"
                  class="relative w-full aspect-square border rounded-lg overflow-hidden group"
                >
                  <img
                    [src]="img"
                    alt="Uploaded"
                    class="w-full h-full object-cover"
                  />
                  <button
                    (click)="removeImage(img)"
                    class="absolute top-1 right-1 bg-white rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-xs sm:text-sm text-red-600 font-bold opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <p class="text-xs sm:text-sm text-gray-500 text-start">
                Drag files here in JPG/PNG | File Size : Max 5MB
              </p>
            </div>

            <!-- File Validation Errors -->
            <div *ngIf="fileValidationErrors.length > 0" class="mt-2">
              <div
                *ngFor="let error of fileValidationErrors"
                class="text-red-500 text-xs sm:text-sm mb-1"
              >
                {{ error }}
              </div>
            </div>
          </div>

          <!-- Note -->
          <div
            class="text-sm sm:text-base leading-5 sm:leading-6 font-bold font-['Uni Neue'] text-[color:var(--Text-Black,#000)] rounded-lg bg-[color:var(--Neutrals-Off-White,#F2F2F2)] p-3 sm:p-4 mb-4"
          >
            Note: Certificate will be submitted for verification. Processing may
            take 2–3 days.
          </div>
        </form>

        <!-- Action Buttons -->
        <div class="flex flex-row justify-between gap-3 mt-6 w-full">
          <button
            type="button"
            (click)="closeModal()"
            class="w-full sm:flex-1 px-4 py-3 sm:py-2 border border-[color:var(--Primary-Deep-Blue,#0A0A5F)] text-[color:var(--Primary-Deep-Blue,#0A0A5F)] rounded-lg text-sm sm:text-base font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            (click)="onSubmit()"
            class="w-full sm:flex-1 px-4 py-3 sm:py-2 bg-[color:var(--Primary-Deep-Blue,#0A0A5F)] text-white rounded-lg text-sm sm:text-base font-medium hover:opacity-90 transition-opacity"
          >
            Save
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile Category Bottom Sheet -->
    <div
      *ngIf="showCategoryBottomSheet && isMobile"
      class="fixed inset-0 z-[60] flex items-end justify-center md:hidden"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black bg-opacity-40"
        (click)="closeCategoryBottomSheet()"
      ></div>

      <!-- Bottom Sheet Content -->
      <div
        class="relative w-full bg-white rounded-t-2xl shadow-lg max-h-[85vh] overflow-hidden animate-slide-up"
      >
        <!-- Header -->
        <div
          class="flex justify-between items-center p-4 border-b border-gray-200 bg-white sticky top-0"
        >
          <h2 class="text-lg font-bold text-[#0A0A5F]">Scrap Category</h2>
          <button
            (click)="closeCategoryBottomSheet()"
            class="text-gray-500 hover:text-gray-700 text-xl p-1"
          >
            ✕
          </button>
        </div>

        <!-- Category Options -->
        <div class="bg-white overflow-y-auto max-h-[calc(85vh-140px)]">
          <div
            *ngFor="let category of scrapList; let last = last"
            class="px-4 py-3"
            [class.border-b]="!last"
            [class.border-gray-200]="!last"
          >
            <label class="flex items-center cursor-pointer">
              <input
                type="radio"
                name="scrapCategoryRadio"
                [value]="category?.name"
                [checked]="selectedCategoryValue === category?.name"
                (change)="selectCategory(category?.name)"
                class="w-5 h-5 text-[#0A0A5F] focus:ring-[#0A0A5F] focus:ring-2 cursor-pointer accent-[#0A0A5F]"
              />
              <span class="ml-3 text-sm sm:text-base text-gray-900">{{
                category?.name
              }}</span>
            </label>
          </div>
        </div>

        <!-- Footer with Proceed button -->
        <div class="p-4 bg-white sticky bottom-0 border-t border-gray-200">
          <button
            (click)="confirmCategorySelection()"
            [disabled]="!selectedCategoryValue"
            class="w-full py-3 px-4 bg-[#0A0A5F] text-white font-medium rounded-lg hover:opacity-90 disabled:bg-gray-400 disabled:cursor-not-allowed transition-opacity"
          >
            Proceed
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile Date Picker Modal -->
    <div
      *ngIf="showDatePicker && isMobile"
      class="fixed inset-0 z-[60] flex items-end justify-center md:hidden"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black bg-opacity-40"
        (click)="closeDatePicker()"
      ></div>

      <!-- Modal Content -->
      <div
        class="relative w-full bg-[#fbfbfb] rounded-t-2xl shadow-lg max-h-[85vh] overflow-hidden animate-slide-up"
      >
        <!-- Calendar Header -->
        <div
          class="flex justify-between items-center p-4 border-b border-gray-600 bg-[#fbfbfb] sticky top-0"
        >
          <div class="flex items-center gap-2">
            <button
              (click)="previousMonth()"
              class="p-1 hover:bg-gray-700 rounded"
            >
              <svg
                class="w-5 h-5 text-black"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>
            <div class="flex items-center gap-1">
              <span class="text-lg font-semibold text-black">{{
                getCurrentMonthYear()
              }}</span>
              <button
                (click)="toggleMonthYearPicker()"
                class="p-1 hover:bg-gray-700 rounded"
              >
                <svg
                  class="w-4 h-4 text-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  ></path>
                </svg>
              </button>
            </div>
            <button (click)="nextMonth()" class="p-1 hover:bg-gray-700 rounded">
              <svg
                class="w-5 h-5 text-black"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Month/Year Picker (when toggled) -->
        <div
          *ngIf="showMonthYearPicker"
          class="p-4 border-b border-gray-600 bg-[#fbfbfb]"
        >
          <div class="grid grid-cols-3 gap-2">
            <select
              [(ngModel)]="selectedYear"
              (change)="onYearChange()"
              class="p-2 border border-gray-500 rounded bg-[#fbfbfb] text-black"
            >
              <option *ngFor="let year of availableYears" [value]="year">
                {{ year }}
              </option>
            </select>
            <select
              [(ngModel)]="selectedMonth"
              (change)="onMonthChange()"
              class="p-2 border border-gray-500 rounded col-span-2 bg-[#fbfbfb] text-black"
            >
              <option
                *ngFor="let month of monthNames; let i = index"
                [value]="i"
              >
                {{ month }}
              </option>
            </select>
          </div>
        </div>

        <!-- Calendar Grid -->
        <div class="p-4 bg-white">
          <!-- Days of Week Header -->
          <div class="grid grid-cols-7 gap-1 mb-2">
            <div
              *ngFor="let day of dayNames"
              class="text-center text-sm font-medium text-gray-500 py-2"
            >
              {{ day }}
            </div>
          </div>

          <!-- Calendar Days -->
          <div class="grid grid-cols-7 gap-1">
            <div
              *ngFor="let day of calendarDays"
              class="text-center py-2 rounded cursor-pointer transition-colors"
              [ngClass]="{
                'text-gray-400': day.isOtherMonth,
                'text-gray-900 hover:bg-gray-100':
                  !day.isOtherMonth && !day.isSelected && !day.isToday,
                'bg-[#307FE1] text-white font-semibold rounded-full':
                  day.isSelected,
                'bg-blue-100 text-blue-700 font-semibold':
                  day.isToday && !day.isSelected,
                'opacity-50 cursor-not-allowed': day.isDisabled,
              }"
              (click)="selectDate(day)"
              [class.pointer-events-none]="day.isDisabled"
            >
              {{ day.date }}
            </div>
          </div>
        </div>

        <!-- Footer with Back and Proceed buttons -->
        <div class="p-4 bg-[#fbfbfb] sticky bottom-0">
          <div class="flex gap-3">
            <button
              (click)="closeDatePicker()"
              class="flex-1 py-3 px-4 border border-gray-500 text-black font-medium rounded-lg hover:bg-gray-700 bg-transparent"
            >
              Back
            </button>
            <button
              (click)="confirmDateSelection()"
              [disabled]="!selectedDateValue"
              class="flex-1 py-3 px-4 bg-[#307FE1] text-white font-medium rounded-lg hover:bg-blue-900 disabled:bg-gray-600 disabled:cursor-not-allowed"
            >
              Proceed
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      @keyframes slideUp {
        from {
          transform: translateY(100%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      .animate-slide-up {
        animation: slideUp 0.3s ease-out forwards;
      }

      .animate-fade-in {
        animation: fadeIn 0.3s ease-out forwards;
      }

      @media (min-width: 640px) {
        .animate-slide-up {
          animation: none;
          transform: none;
        }
      }

      /* Ensure placeholders are visible on mobile */
      input[type='text']::placeholder {
        color: #9ca3af !important;
        opacity: 1 !important;
        font-weight: normal !important;
      }

      input[type='text']::-webkit-input-placeholder {
        color: #9ca3af !important;
        opacity: 1 !important;
        font-weight: normal !important;
      }

      input[type='text']::-moz-placeholder {
        color: #9ca3af !important;
        opacity: 1 !important;
        font-weight: normal !important;
      }

      input[type='text']:-ms-input-placeholder {
        color: #9ca3af !important;
        opacity: 1 !important;
        font-weight: normal !important;
      }

      /* Mobile-specific placeholder styling */
      @media (max-width: 767px) {
        input[type='text']::placeholder {
          color: #9ca3af !important;
          opacity: 1 !important;
          font-weight: 400 !important;
          font-size: 0.875rem !important;
        }

        input[type='text']::-webkit-input-placeholder {
          color: #9ca3af !important;
          opacity: 1 !important;
          font-weight: 400 !important;
          font-size: 0.875rem !important;
        }
      }
    `,
  ],
})
export class ScrapCertificateModalComponent implements OnInit, OnDestroy {
  showScrapCertificateModal = false;
  certificateForm!: FormGroup;
  scrapList: any[] = [];
  ImageAray: any[] = [];
  fileValidationErrors: string[] = [];
  private subscriptions: Subscription = new Subscription();
  private destroy$ = new Subject<void>();

  // Category bottom sheet properties
  showCategoryBottomSheet: boolean = false;
  selectedCategoryValue: string = '';
  isMobile: boolean = false;

  // Date picker properties
  showDatePicker: boolean = false;
  currentCalendarDate: Date = new Date();
  selectedDateValue: Date | null = null;
  selectedYear: number = new Date().getFullYear();
  selectedMonth: number = new Date().getMonth();
  showMonthYearPicker: boolean = false;
  calendarDays: any[] = [];
  dayNames: string[] = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  monthNames: string[] = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  availableYears: number[] = [];
  currentDateField: 'issueDate' | 'expiryDate' | null = null;

  // Custom validator to ensure expiry date is greater than issue date
  dateRangeValidator(control: AbstractControl): ValidationErrors | null {
    const issueDate = control.get('issueDate')?.value;
    const expiryDate = control.get('expiryDate')?.value;

    if (issueDate && expiryDate) {
      const issue = new Date(issueDate);
      const expiry = new Date(expiryDate);

      if (expiry <= issue) {
        return { dateRange: true };
      }
    }
    return null;
  }

  // File validation function
  private validateFile(file: File): { isValid: boolean; error?: string } {
    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: `File "${file.name}" is not a valid image format. Only JPG and PNG files are allowed.`,
      };
    }

    // Check file size (5MB = 5 * 1024 * 1024 bytes)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        isValid: false,
        error: `File "${file.name}" is too large. Maximum file size is 5MB.`,
      };
    }

    return { isValid: true };
  }

  constructor(
    private fb: FormBuilder,
    private scrapCertificateService: ScrapCertificateService,
    private cartService: CartService,
    private loaderService: LoaderService,
    private apiService: ApiService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private cdr: ChangeDetectorRef,
    private notificationService: NotificationService,
    private modalService: ModalService,
  ) {
    this.certificateForm = this.fb.group(
      {
        scrapCategory: ['', Validators.required],
        certificateName: [
          '',
          [
            Validators.required,
            Validators.pattern(SCRAP_CERT_NAME_PATTERN),
            this.noLeadingSpaceValidator.bind(this),
          ],
        ],
        certificateNumber: [
          '',
          [
            Validators.required,
            Validators.pattern(SCRAP_CERT_NUMBER_PATTERN),
            this.noLeadingSpaceValidator.bind(this),
          ],
        ],
        issuingAuthority: [
          '',
          [
            Validators.required,
            Validators.pattern(SCRAP_CERT_NAME_PATTERN),
            this.noLeadingSpaceValidator.bind(this),
          ],
        ],
        companyName: [
          '',
          [
            Validators.required,
            Validators.pattern(SCRAP_CERT_NAME_PATTERN),
            this.noLeadingSpaceValidator.bind(this),
          ],
        ],
        issueDate: ['', Validators.required],
        expiryDate: ['', Validators.required],
      },
      { validators: this.dateRangeValidator },
    );
  }

  ngOnInit(): void {
    // Check screen size and set up resize listener
    this.checkScreenSize();
    this.initializeDatePicker();

    // Subscribe to modal visibility changes
    this.scrapCertificateService.showScrapCertificateModal$
      .pipe(takeUntil(this.destroy$))
      .subscribe((show) => {
        this.showScrapCertificateModal = show;
        this.cdr.markForCheck(); // Use markForCheck for OnPush
        if (show) {
          // this.notificationService.info('Please upload scrap certificates to proceed');
          this.loadScrapList();
          // Prevent body scrolling when modal is open
          if (isPlatformBrowser(this.platformId)) {
            this.modalService.openModal('scrap-cert', () => this.closeModal());

          }
        } else {
          // Restore body scrolling when modal is closed
          if (isPlatformBrowser(this.platformId)) {
            this.modalService.closeModal('scrap-cert');

          }
          this.showCategoryBottomSheet = false;
          this.showDatePicker = false;
        }
      });
  }

  checkScreenSize(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.isMobile = window.innerWidth < 768;
      window.addEventListener('resize', () => {
        this.isMobile = window.innerWidth < 768;
        this.cdr.markForCheck();
      });
    }
  }

  initializeDatePicker(): void {
    // Generate available years (current year ± 50 years)
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 50; i <= currentYear + 50; i++) {
      this.availableYears.push(i);
    }
  }

  openDatePicker(fieldName: 'issueDate' | 'expiryDate', event?: Event): void {
    // Only show custom picker on mobile
    if (!this.isMobile) {
      return; // Let native date picker handle desktop
    }

    if (event) {
      event.preventDefault();
    }

    this.currentDateField = fieldName;

    // Initialize with current form value or today
    const formDate = this.certificateForm.get(fieldName)?.value;
    if (formDate) {
      const date = new Date(formDate);
      if (!isNaN(date.getTime())) {
        this.currentCalendarDate = new Date(date);
        this.selectedDateValue = new Date(date);
        this.selectedYear = date.getFullYear();
        this.selectedMonth = date.getMonth();
      }
    } else {
      this.currentCalendarDate = new Date();
      this.selectedYear = this.currentCalendarDate.getFullYear();
      this.selectedMonth = this.currentCalendarDate.getMonth();
      this.selectedDateValue = null;
    }

    this.showMonthYearPicker = false;
    this.generateCalendar();
    this.showDatePicker = true;
  }

  closeDatePicker(): void {
    this.showDatePicker = false;
    this.showMonthYearPicker = false;
    this.currentDateField = null;
  }

  generateCalendar(): void {
    const year = this.currentCalendarDate.getFullYear();
    const month = this.currentCalendarDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7; // Convert Sunday=0 to Monday=0

    this.calendarDays = [];

    // Add previous month's trailing days
    if (startingDayOfWeek > 0) {
      const prevMonthLastDay = new Date(year, month, 0).getDate();
      for (let i = startingDayOfWeek - 1; i >= 0; i--) {
        this.calendarDays.push({
          date: prevMonthLastDay - i,
          isOtherMonth: true,
          isSelected: false,
          isToday: false,
          isDisabled: false,
          fullDate: new Date(year, month - 1, prevMonthLastDay - i),
        });
      }
    }

    // Add current month's days
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      currentDate.setHours(0, 0, 0, 0);

      const isSelected =
        this.selectedDateValue &&
        currentDate.getTime() === this.selectedDateValue.getTime();
      const isToday = currentDate.getTime() === today.getTime();
      const isDisabled = false; // Allow all dates for certificate dates

      this.calendarDays.push({
        date: day,
        isOtherMonth: false,
        isSelected: isSelected,
        isToday: isToday,
        isDisabled: isDisabled,
        fullDate: currentDate,
      });
    }

    // Add next month's leading days to fill the grid
    const remainingDays = 42 - this.calendarDays.length; // 6 rows * 7 days
    for (let day = 1; day <= remainingDays; day++) {
      this.calendarDays.push({
        date: day,
        isOtherMonth: true,
        isSelected: false,
        isToday: false,
        isDisabled: false,
        fullDate: new Date(year, month + 1, day),
      });
    }
  }

  previousMonth(): void {
    this.currentCalendarDate = new Date(
      this.currentCalendarDate.getFullYear(),
      this.currentCalendarDate.getMonth() - 1,
      1,
    );
    this.selectedYear = this.currentCalendarDate.getFullYear();
    this.selectedMonth = this.currentCalendarDate.getMonth();
    this.generateCalendar();
  }

  nextMonth(): void {
    this.currentCalendarDate = new Date(
      this.currentCalendarDate.getFullYear(),
      this.currentCalendarDate.getMonth() + 1,
      1,
    );
    this.selectedYear = this.currentCalendarDate.getFullYear();
    this.selectedMonth = this.currentCalendarDate.getMonth();
    this.generateCalendar();
  }

  toggleMonthYearPicker(): void {
    this.showMonthYearPicker = !this.showMonthYearPicker;
  }

  onYearChange(): void {
    this.currentCalendarDate = new Date(
      this.selectedYear,
      this.selectedMonth,
      1,
    );
    this.generateCalendar();
    this.showMonthYearPicker = false;
  }

  onMonthChange(): void {
    this.currentCalendarDate = new Date(
      this.selectedYear,
      this.selectedMonth,
      1,
    );
    this.generateCalendar();
    this.showMonthYearPicker = false;
  }

  selectDate(day: any): void {
    if (day.isDisabled || day.isOtherMonth) return;

    this.selectedDateValue = new Date(day.fullDate);
    this.generateCalendar();
  }

  confirmDateSelection(): void {
    if (this.selectedDateValue && this.currentDateField) {
      const formattedDate = this.formatDateToYYYYMMDD(
        this.selectedDateValue.toISOString(),
      );
      this.certificateForm.patchValue({
        [this.currentDateField]: formattedDate,
      });
      this.certificateForm.get(this.currentDateField)?.markAsTouched();
      this.onDateChange();
      const fieldLabel =
        this.currentDateField === 'issueDate' ? 'Issue date' : 'Expiry date';
      this.notificationService.success(`${fieldLabel} selected`);
      this.closeDatePicker();
      this.cdr.markForCheck();
    }
  }

  getCurrentMonthYear(): string {
    return `${this.monthNames[this.currentCalendarDate.getMonth()]} ${this.currentCalendarDate.getFullYear()}`;
  }

  formatDateToYYYYMMDD(dateString: string): string {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  ngOnDestroy(): void {
    // Clean up all subscriptions
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.unsubscribe();
    this.modalService.closeModal('scrap-cert');
  }

  closeModal(): void {
    if (this.certificateForm.dirty) {
      // this.notificationService.info('Certificate form closed. Changes discarded.');
    }
    this.scrapCertificateService.closeScrapCertificateModal();
    this.resetForm();
    this.showCategoryBottomSheet = false;
  }

  openCategoryBottomSheet(): void {
    // Only show bottom sheet on mobile
    if (!this.isMobile) {
      return; // Let native select handle desktop
    }

    // Initialize with current form value
    const currentValue = this.certificateForm.get('scrapCategory')?.value || '';
    this.selectedCategoryValue = currentValue;
    this.showCategoryBottomSheet = true;
  }

  closeCategoryBottomSheet(): void {
    this.showCategoryBottomSheet = false;
  }

  selectCategory(categoryName: string): void {
    this.selectedCategoryValue = categoryName;
  }

  confirmCategorySelection(): void {
    if (this.selectedCategoryValue) {
      this.certificateForm.patchValue({
        scrapCategory: this.selectedCategoryValue,
      });
      this.certificateForm.get('scrapCategory')?.markAsTouched();
      // this.notificationService.success(
      //   `${this.selectedCategoryValue} category selected`,
      // );
      this.cdr.markForCheck();
    }
    this.closeCategoryBottomSheet();
  }

  getSelectedCategoryDisplay(): string {
    const selectedValue = this.certificateForm.get('scrapCategory')?.value;
    if (!selectedValue) {
      return 'Select any one Category';
    }
    return selectedValue;
  }

  onBackdropClick(event: Event): void {
    // Only close modal if clicking on the backdrop itself, not on modal content
    if (event.target === event.currentTarget) {
      this.closeModal();
    }
  }

  removeImage(img: string): void {
    const index = this.ImageAray.indexOf(img);
    if (index > -1) {
      this.ImageAray.splice(index, 1);
      // this.notificationService.success('Certificate image removed');
      this.cdr.markForCheck();
    }
  }

  triggerFileInput(fileInput: HTMLInputElement): void {
    if (fileInput) {
      fileInput.click();
    }
  }

  onFileUpload(event: any): void {
    const files = event.target.files;
    this.fileValidationErrors = []; // Clear previous errors

    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const validation = this.validateFile(file);

        if (!validation.isValid) {
          this.fileValidationErrors.push(validation.error!);
          // this.notificationService.error(validation.error!);
          continue;
        }

        const reader = new FileReader();
        reader.onload = (e: any) => {
          this.ImageAray.push(e.target.result);
          // this.notificationService.success(
          //   `File "${file.name}" uploaded successfully`,
          // );
          this.cdr.markForCheck();
        };
        reader.onerror = () => {
          console.error('Error reading file:', file.name);
          const errorMsg = `Error reading file: ${file.name}`;
          this.fileValidationErrors.push(errorMsg);
          // this.notificationService.error(errorMsg);
          this.cdr.markForCheck();
        };
        reader.readAsDataURL(file);
      }
    }
    // Reset the input value to allow selecting the same file again
    event.target.value = '';
  }

  onFileSelected(event: any): void {
    const files = event.target.files;
    this.fileValidationErrors = []; // Clear previous errors

    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const validation = this.validateFile(file);

        if (!validation.isValid) {
          this.fileValidationErrors.push(validation.error!);
          // this.notificationService.error(validation.error!);
          continue;
        }

        const reader = new FileReader();
        reader.onload = (e: any) => {
          this.ImageAray.push(e.target.result);
          // this.notificationService.success(
          //   `File "${file.name}" added successfully`,
          // );
          this.cdr.markForCheck();
        };
        reader.onerror = () => {
          console.error('Error reading file:', file.name);
          const errorMsg = `Error reading file: ${file.name}`;
          this.fileValidationErrors.push(errorMsg);
          // this.notificationService.error(errorMsg);
          this.cdr.markForCheck();
        };
        reader.readAsDataURL(file);
      }
    }
    // Reset the input value to allow selecting the same file again
    event.target.value = '';
  }

  onDateChange(): void {
    // Trigger validation when dates change
    this.certificateForm.updateValueAndValidity();
  }

  onSubmit(): void {
    if (this.certificateForm.valid && this.ImageAray.length > 0) {
      this.saveScrapCertificate();
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.certificateForm.controls).forEach((key) => {
        this.certificateForm.get(key)?.markAsTouched();
      });
      if (this.ImageAray.length === 0) {
        this.notificationService.error(
          'Please upload at least one certificate image',
        );
      } else {
        this.notificationService.error(
          'Please fill in all required fields correctly',
        );
      }
      this.cdr.markForCheck();
    }
  }

  private saveScrapCertificate(): void {
    const productData = this.scrapCertificateService.getScrapProductData();
    if (!productData) {
      console.error('No product data available for scrap certificate');
      // this.notificationService.error(
      //   'Product data not available. Please try again.',
      // );
      return;
    }

    this.loaderService.show();

    try {
      // Create FormData for form fields
      const formData = new FormData();

      // Add form fields to FormData
      formData.append(
        'scrap_category',
        this.certificateForm.get('scrapCategory')?.value || '',
      );
      formData.append(
        'cert_name',
        this.certificateForm.get('certificateName')?.value || '',
      );
      formData.append(
        'cert_no',
        this.certificateForm.get('certificateNumber')?.value || '',
      );
      formData.append(
        'issuing_authority',
        this.certificateForm.get('issuingAuthority')?.value || '',
      );
      formData.append(
        'company_name',
        this.certificateForm.get('companyName')?.value || '',
      );
      formData.append(
        'issue_date',
        this.certificateForm.get('issueDate')?.value || '',
      );
      formData.append(
        'expiry_date',
        this.certificateForm.get('expiryDate')?.value || '',
      );

      // Add images as binary data in array
      // this.ImageAray.forEach((image, index) => {
      //   if (image instanceof File) {
      //     // If it's a File object, append with array notation
      //     formData.append(`certificate_${index}`, image);
      //   } else if (typeof image === 'string') {
      //     // If it's a base64 string, convert to blob and append
      //     const blob = this.convertBase64ToBlob(image);
      //     formData.append(`certificate_${index}`, blob);
      //   }
      // });

      this.apiService
        .add_scrap_certificate(formData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (value) => {
            this.loaderService.hide();
            if (value.success) {
              // this.notificationService.success(
              //   'Certificate submitted successfully. Adding item to cart...',
              // );
              // Close modal after successful submission
              this.closeModal();
              // Add item to cart after successful certificate upload
              this.addScrapItemToCart(productData);
            } else {
              // console.warn('Certificate submission failed:', value.message);
              //   this.notificationService.error(
              //     value.message ||
              //       'Failed to submit certificate. Please try again.',
              //   );
            }
            this.cdr.markForCheck();
          },
          error: (err) => {
            this.loaderService.hide();
            console.error('Error submitting certificate:', err);
              // this.notificationService.error(
              //   err?.error?.data?.msg ||
              //     'Error submitting certificate. Please try again.',
              // );
            this.cdr.markForCheck();
          },
        });
    } catch (error) {
      this.loaderService.hide();
      console.error('Error in certificate submission:', error);
      // this.notificationService.error(
      //   'Error submitting certificate. Please try again.',
      // );
      this.cdr.markForCheck();
    }
  }

  private addScrapItemToCart(productData: any): void {
    const partNumber =
      productData.part_number || productData.part_detail?.part_number;
    if (!partNumber) {
      console.error('No part number available for adding to cart');
      // this.notificationService.error(
      //   'Part number not available. Cannot add to cart.',
      // );
      return;
    }

    // Get quantity and unit for scrap parts (default values)
    const quantity = 1; // Default quantity
    const unit = 'Kg'; // Default unit

    this.loaderService.show();
    this.cartService
      .addToCart(partNumber, 'Store Pickup', quantity, unit)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loaderService.hide();
          // this.notificationService.success('Scrap item added to cart successfully');
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error adding scrap item to cart:', err);
          // this.notificationService.error(
          //   err?.error?.data?.msg ||
          //     'Failed to add item to cart. Please try again.',
          // );
          this.loaderService.hide();
          this.cdr.markForCheck();
        },
        complete: () => {
          this.loaderService.hide();
        },
      });
  }

  private loadScrapList(): void {
    // For now, use a mock list. Replace with actual API call when available
    this.loaderService.show();
    try {
      const payload = {}; // Provide appropriate payload as required by the API

      this.apiService
        .get_category(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (value) => {
            if (value.success) {
              this.scrapList = value?.data?.scrap_list;
              // if (this.scrapList.length > 0) {
              //   this.notificationService.success(`${this.scrapList.length} scrap categories loaded`);
              // }
            } else {
              console.warn('Category API returned success: false');
              this.scrapList = [];
              // this.notificationService.error('Failed to load scrap categories');
            }
            this.loaderService.hide();

            // Pre-select scrap category based on current product, if available
            const productData =
              this.scrapCertificateService.getScrapProductData();
            const productScrapCategory =
              productData?.part_detail?.scrap_category ||
              productData?.scrap_category;
            if (productScrapCategory) {
              const existsInList = this.scrapList?.some(
                (c: any) => c?.name === productScrapCategory,
              );
              if (existsInList) {
                this.certificateForm
                  .get('scrapCategory')
                  ?.setValue(productScrapCategory);
                // this.notificationService.info(`Pre-selected category: ${productScrapCategory}`);
              }
            }
            this.cdr.markForCheck();
          },
          error: (err) => {
            console.error('Error loading category list:', err);
            this.scrapList = [];
            // this.notificationService.error(
            //   err?.error?.data?.msg ||
            //     'Failed to load scrap categories. Please try again.',
            // );
            this.loaderService.hide();
            this.cdr.markForCheck();
          },
        });
    } catch (error) {
      console.error('Error in category list:', error);
      this.scrapList = [];
      // this.notificationService.error(
      //   'Error loading scrap categories. Please try again.',
      // );
      this.loaderService.hide();
      this.cdr.markForCheck();
    }
  }

  private convertBase64ToBlob(base64String: string): Blob {
    const byteCharacters = atob(base64String.split(',')[1]);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: 'image/jpeg' });
  }

  private resetForm(): void {
    this.certificateForm.reset();
    this.ImageAray = [];
    this.fileValidationErrors = [];
  }

  // Form validation methods
  isFieldInvalid(fieldName: string): boolean {
    const field = this.certificateForm.get(fieldName);
    const fieldInvalid = !!(
      field &&
      field.invalid &&
      (field.dirty || field.touched)
    );

    // Also check for form-level date range validation error
    const hasDateRangeError = !!(
      this.certificateForm.errors &&
      this.certificateForm.errors['dateRange'] &&
      (fieldName === 'issueDate' || fieldName === 'expiryDate') &&
      (this.certificateForm.get('issueDate')?.dirty ||
        this.certificateForm.get('expiryDate')?.dirty ||
        this.certificateForm.get('issueDate')?.touched ||
        this.certificateForm.get('expiryDate')?.touched)
    );

    return fieldInvalid || hasDateRangeError;
  }

  getFieldError(fieldName: string): string {
    const field = this.certificateForm.get(fieldName);
    let controlValue = fieldName;
    if (fieldName === 'certificateName') {
      controlValue = 'Enter Certificate Name';
    } else if (fieldName === 'certificateNumber') {
      controlValue = 'Enter Certificate Number';
    } else if (fieldName === 'issuingAuthority') {
      controlValue = 'Enter Issuing Authority';
    } else if (fieldName === 'companyName') {
      controlValue = 'Enter Company Name';
    } else if (fieldName === 'issueDate') {
      controlValue = 'Enter Issue Date';
    } else if (fieldName === 'expiryDate') {
      controlValue = 'Enter Expiry Date';
    }
    const label = this.getFieldLabel(fieldName);
    if (field && field.errors && (field.dirty || field.touched)) {
      if (field.errors['required']) {
        return `${label} is required`;
      }
      if (field.errors['pattern']) {
        if (fieldName === 'certificateNumber') {
          return 'Certificate Number can only contain letters, numbers, and allowed characters (- / . _ # :)';
        }
        return `${label} can only contain letters, numbers, and allowed characters (- / . , & ( ) ' : _ #)`;
      }
      if (field.errors['leadingSpace']) {
        return 'Leading spaces are not allowed';
      }
      if (field.errors['email']) {
        return 'Please enter a valid email address';
      }
      if (field.errors['minlength']) {
        return `${label} must be at least ${field.errors['minlength'].requiredLength} characters`;
      }
    }

    // Check for form-level date range validation error
    if (
      this.certificateForm.errors &&
      this.certificateForm.errors['dateRange'] &&
      (fieldName === 'issueDate' || fieldName === 'expiryDate') &&
      (this.certificateForm.get('issueDate')?.dirty ||
        this.certificateForm.get('expiryDate')?.dirty ||
        this.certificateForm.get('issueDate')?.touched ||
        this.certificateForm.get('expiryDate')?.touched)
    ) {
      if (fieldName === 'expiryDate') {
        return 'Expiry date must be greater than issue date';
      }
    }

    return '';
  }

  getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      scrapCategory: 'Scrap Category',
      certificateName: 'Certificate Name',
      certificateNumber: 'Certificate Number',
      issuingAuthority: 'Issuing Authority',
      companyName: 'Company Name',
      issueDate: 'Issue Date',
      expiryDate: 'Expiry Date',
    };
    return labels[fieldName] || fieldName;
  }

  // Prevent leading space and disallowed characters for name/text fields
  preventLeadingSpace(event: KeyboardEvent, input: any): void {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    const key = event.key;
    if (NAVIGATION_KEYS.includes(key)) {
      return;
    }

    // Prevent space if it's the first character
    if (key === ' ' && (!input?.value || input?.selectionStart === 0)) {
      event.preventDefault();
      return;
    }

    // Allow letters, numbers, spaces, and allowed special characters: - / . , & ( ) ' : _ #
    const allowedRegex = /^[a-zA-Z0-9\s\-\/.,&()':_#]$/;
    if (!allowedRegex.test(key)) {
      event.preventDefault();
    }
  }

  // Prevent leading space and disallowed characters for certificate number field
  preventLeading(event: KeyboardEvent, input: any): void {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    const key = event.key;
    if (NAVIGATION_KEYS.includes(key)) {
      return;
    }

    // Prevent space if it's the first character
    if (key === ' ' && (!input?.value || input?.selectionStart === 0)) {
      event.preventDefault();
      return;
    }

    // Allow letters, numbers, spaces, and allowed certificate number special characters: - / . _ # :
    const allowedRegex = /^[a-zA-Z0-9\s\-\/._#:]$/;
    if (!allowedRegex.test(key)) {
      event.preventDefault();
    }
  }

  // Trim leading spaces from input fields
  trimLeadingSpace(event: Event, fieldName: string): void {
    const input = event.target as HTMLInputElement;
    if (input && input.value && input.value.startsWith(' ')) {
      const trimmedValue = input.value.trimStart();
      this.certificateForm
        .get(fieldName)
        ?.setValue(trimmedValue, { emitEvent: false });
      // Update the input value directly to reflect the change immediately
      input.value = trimmedValue;
    }
  }

  // Validator to check for leading spaces
  private noLeadingSpaceValidator(
    control: AbstractControl,
  ): ValidationErrors | null {
    const value = control.value;
    if (value && typeof value === 'string') {
      // Trim leading spaces and update the control value
      const trimmedValue = value.trimStart();
      if (trimmedValue !== value) {
        control.setValue(trimmedValue, { emitEvent: false });
      }
      // Check if there are still leading spaces (shouldn't happen after trim, but just in case)
      if (/^\s/.test(trimmedValue)) {
        return { leadingSpace: true };
      }
    }
    return null;
  }
}
